import jsPDF from "jspdf";
import { buildDefaultCoverLetterParagraphs } from "./accountingCoverLetter";
import {
  countRegistrationGuests,
  shouldUseLateFeesForRegistration,
  shouldWaiveRegistrantMemberFee,
} from "./invoiceCalculations";
import { getSpecialEventGuestsForProgram } from "./specialEventGuestNames";
import { normalizeRegistrationGuests } from "./registrationGuests";

/** jsPDF built-in fonts (Helvetica) only support WinAnsi; UTF-8 needs embedded TTF. */
const INVOICE_FONT = "NotoSans";

const NOTO_SANS_TTF = {
  regular:
    "https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoSans/NotoSans-Regular.ttf",
  bold: "https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoSans/NotoSans-Bold.ttf",
  italic:
    "https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoSans/NotoSans-Italic.ttf",
} as const;

let cachedNotoSansBase64: {
  regular: string;
  bold: string;
  italic: string;
} | null = null;

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function loadNotoSansBase64(): Promise<
  NonNullable<typeof cachedNotoSansBase64>
> {
  if (cachedNotoSansBase64) {
    return cachedNotoSansBase64;
  }
  const [regRes, boldRes, italicRes] = await Promise.all([
    fetch(NOTO_SANS_TTF.regular),
    fetch(NOTO_SANS_TTF.bold),
    fetch(NOTO_SANS_TTF.italic),
  ]);
  if (!regRes.ok || !boldRes.ok || !italicRes.ok) {
    throw new Error("Could not load Noto Sans fonts for invoice PDF");
  }
  const [regular, bold, italic] = await Promise.all([
    regRes.arrayBuffer(),
    boldRes.arrayBuffer(),
    italicRes.arrayBuffer(),
  ]);
  cachedNotoSansBase64 = {
    regular: arrayBufferToBase64(regular),
    bold: arrayBufferToBase64(bold),
    italic: arrayBufferToBase64(italic),
  };
  return cachedNotoSansBase64;
}

function embedNotoSans(
  doc: jsPDF,
  data: NonNullable<typeof cachedNotoSansBase64>,
): void {
  doc.addFileToVFS("NotoSans-Regular.ttf", data.regular);
  doc.addFont("NotoSans-Regular.ttf", INVOICE_FONT, "normal");
  doc.addFileToVFS("NotoSans-Bold.ttf", data.bold);
  doc.addFont("NotoSans-Bold.ttf", INVOICE_FONT, "bold");
  doc.addFileToVFS("NotoSans-Italic.ttf", data.italic);
  doc.addFont("NotoSans-Italic.ttf", INVOICE_FONT, "italic");
}

/** Left edge of description column; line-item concepts are left-aligned. Lines wrap within maxWidth. */
const DESC_LEFT_X = 80;
const DESC_MAX_WIDTH = 365;
const AMOUNT_X = 460;
/** Right edge for the amount column; numbers are right-aligned to this x. */
const AMOUNT_RIGHT_X = 545;
const LINE_HEIGHT = 12;
const PAGE_HEIGHT = 792;

const INVOICE_BOX = {
  x: 70,
  width: 480,
  top: 250,
  minHeight: 140,
  headerHeight: 20,
  totalRowHeight: 20,
  eventTitleOffset: 40,
  itemsStartOffset: 63,
  itemsBottomPadding: 8,
  totalTextOffset: 14,
  amountDividerX: 450,
} as const;

type InvoicePDFLineItem = {
  description: string;
  amountText: string;
};

function getCurrencySymbols(currency: string): {
  positive: string;
  negative: string;
} {
  const isUsd = currency.toUpperCase() === "USD";
  return {
    positive: isUsd ? "$ USD " : "EURO ",
    negative: isUsd ? "$ USD -" : "-EURO ",
  };
}

function formatAmount(amount: number): string {
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function measureLineItemsHeight(
  doc: jsPDF,
  fontFamily: string,
  lineItems: InvoicePDFLineItem[],
  fontSize = 10,
): number {
  doc.setFont(fontFamily, "normal");
  doc.setFontSize(fontSize);
  return lineItems.reduce((height, item) => {
    const lines = doc.splitTextToSize(item.description, DESC_MAX_WIDTH);
    return height + Math.max(1, lines.length) * LINE_HEIGHT;
  }, 0);
}

function calculateInvoiceBoxLayout(
  doc: jsPDF,
  fontFamily: string,
  lineItems: InvoicePDFLineItem[],
  itemsStartOffset: number = INVOICE_BOX.itemsStartOffset,
  totalRowHeight: number = INVOICE_BOX.totalRowHeight,
  extraContentHeight = 0,
): {
  boxHeight: number;
  boxBottom: number;
  totalBarY: number;
  totalTextY: number;
  itemsStartY: number;
  layoutShift: number;
  totalRowHeight: number;
} {
  const { top, minHeight, itemsBottomPadding, totalTextOffset } = INVOICE_BOX;

  const itemsHeight = measureLineItemsHeight(doc, fontFamily, lineItems);
  const contentEndOffset =
    itemsStartOffset + itemsHeight + extraContentHeight + itemsBottomPadding;
  const minContentEndOffset = minHeight - totalRowHeight;
  const totalBarY = top + Math.max(contentEndOffset, minContentEndOffset);
  const boxHeight = totalBarY + totalRowHeight - top;

  return {
    boxHeight,
    boxBottom: top + boxHeight,
    totalBarY,
    totalTextY: totalBarY + totalTextOffset,
    itemsStartY: top + itemsStartOffset,
    layoutShift: boxHeight - minHeight,
    totalRowHeight,
  };
}

function buildInvoicePDFLineItems(params: {
  person: InvoicePDFData["person"];
  regAmount: number;
  guestNames?: string[];
  guestName?: string;
  guestName2?: string;
  applicableGuestFee: number;
  program?: InvoicePDFData["event"]["program"];
  selectedSpecialEventIds?: string[];
  specialEventGuestNames?: Record<string, string | string[]>;
  eventTitle: string;
  additionalFeeValue: number;
  creditAmount: number;
  paidAmount: number;
  isPaid: boolean;
  isPrepaid?: boolean;
  currency: string;
}): InvoicePDFLineItem[] {
  const {
    person,
    regAmount,
    guestNames,
    guestName,
    guestName2,
    applicableGuestFee,
    program,
    selectedSpecialEventIds,
    specialEventGuestNames = {},
    eventTitle,
    additionalFeeValue,
    creditAmount,
    paidAmount,
    isPaid,
    isPrepaid = false,
    currency,
  } = params;

  const { positive: currencySymbol, negative: currencySymbolNeg } =
    getCurrencySymbols(currency);
  const items: InvoicePDFLineItem[] = [];
  const regName = person.name || "N/A";
  const registrationFeeLabel = isPrepaid
    ? `${regName} - Registration Fee: Prepaid`
    : `${regName} - Registration Fee`;

  items.push({
    description: registrationFeeLabel,
    amountText: `${currencySymbol}${regAmount.toFixed(2)}`,
  });

  const resolvedGuestNames =
    guestNames && guestNames.length > 0
      ? guestNames
      : [guestName, guestName2].filter(
          (n): n is string => typeof n === "string" && n.trim().length > 0,
        );

  for (const name of resolvedGuestNames) {
    items.push({
      description: `${name} - Guest Registration Fee`,
      amountText: `${currencySymbol}${applicableGuestFee.toFixed(2)}`,
    });
  }

  const regNameForSpecial = person.name || "N/A";
  const eventTitleForSpecial = eventTitle || "Event";
  const idsToRender = selectedSpecialEventIds ?? [];
  if (program?.length && idsToRender.length > 0) {
    const progsToRender = idsToRender
      .map((id) => program.find((p) => p.id === id))
      .filter(Boolean);

    for (const prog of progsToRender) {
      if (!prog) {
        continue;
      }
      const programFee = prog.fee ?? 0;
      const specialEventTitle =
        prog.registrationPageSessionTitle ||
        (eventTitleForSpecial && prog.title
          ? `${eventTitleForSpecial} - ${prog.title}`
          : prog.title) ||
        "Special Event";
      items.push({
        description: `${regNameForSpecial} - Event Fee: ${specialEventTitle}`,
        amountText: `${currencySymbol}${programFee.toFixed(2)}`,
      });
    }

    for (const prog of progsToRender) {
      if (!prog) {
        continue;
      }
      const key = (prog as { id?: string }).id ?? program.indexOf(prog);
      const programId = String(key);
      const guests = getSpecialEventGuestsForProgram(
        specialEventGuestNames,
        programId,
      );
      if (guests.length === 0) {
        continue;
      }
      const guestFeeAmount = prog.specialEventGuestFee ?? 0;
      const specialEventTitle =
        prog.registrationPageSessionTitle ||
        (eventTitleForSpecial && prog.title
          ? `${eventTitleForSpecial} - ${prog.title}`
          : prog.title) ||
        "Special Event";
      for (const specialGuestName of guests) {
        items.push({
          description: `${specialGuestName.trim()} - Special Event Guest Fee: ${specialEventTitle}`,
          amountText: `${currencySymbol}${guestFeeAmount.toFixed(2)}`,
        });
      }
    }
  }

  if (additionalFeeValue > 0) {
    items.push({
      description: "Additional Fee",
      amountText: `${currencySymbol}${additionalFeeValue.toFixed(2)}`,
    });
  }

  if (creditAmount > 0) {
    items.push({
      description: "Credit Applied",
      amountText: `${currencySymbolNeg}${creditAmount.toFixed(2)}`,
    });
  }

  if (!isPaid && paidAmount > 0) {
    items.push({
      description: "Amount Paid",
      amountText: `${currencySymbolNeg}${paidAmount.toFixed(2)}`,
    });
  }

  return items;
}

function buildCustomInvoicePDFLineItems(
  items: { description: string; amountText: string }[],
  params: {
    currency: string;
    paidAmount: number;
    isPaid: boolean;
    paidLabel?: string;
    creditAppliedDisplay?: number;
  },
): InvoicePDFLineItem[] {
  const result: InvoicePDFLineItem[] = [...items];
  const { negative: currencySymbolNeg } = getCurrencySymbols(params.currency);
  const creditApplied = Number(params.creditAppliedDisplay) || 0;
  if (creditApplied > 0) {
    result.push({
      description: params.paidLabel?.trim() || "Credit Balance Applied",
      amountText: `${currencySymbolNeg}${creditApplied.toFixed(2)}`,
    });
  } else if (!params.isPaid && params.paidAmount > 0) {
    result.push({
      description: params.paidLabel?.trim() || "Amount Paid",
      amountText: `${currencySymbolNeg}${params.paidAmount.toFixed(2)}`,
    });
  }
  return result;
}

/**
 * Draws an amount so the currency label (e.g. "$ USD") is left-aligned to the
 * amount column while the numeric value is right-aligned to its right edge.
 * Any leading minus sign stays attached to the number.
 */
function drawJustifiedAmount(doc: jsPDF, amountText: string, y: number): void {
  const match = /^(.*?)(-?[\d][\d.,]*)\s*$/.exec(amountText);
  if (!match) {
    doc.text(amountText, AMOUNT_X, y);
    return;
  }
  const prefix = match[1].trim();
  const number = match[2];
  if (prefix) {
    doc.text(prefix, AMOUNT_X, y);
  }
  doc.text(number, AMOUNT_RIGHT_X - doc.getTextWidth(number), y);
}

function drawDescriptionAmountRow(
  doc: jsPDF,
  fontFamily: string,
  description: string,
  amountText: string,
  y: number,
  fontSize = 10,
): number {
  doc.setFont(fontFamily, "normal");
  doc.setFontSize(fontSize);
  const lines = doc.splitTextToSize(description, DESC_MAX_WIDTH);
  lines.forEach((line: string, i: number) => {
    doc.text(line, DESC_LEFT_X, y + i * LINE_HEIGHT);
  });
  drawJustifiedAmount(doc, amountText, y);
  return y + Math.max(1, lines.length) * LINE_HEIGHT;
}

export interface InvoiceCoverLetter {
  /** Formatted current date, e.g. "4 March 2026". */
  dateDisplay: string;
  /** Recipient firm/office name shown in the address block. */
  recipientName: string;
  /** Pre-formatted recipient address lines (one entry per rendered line). */
  recipientAddressLines: string[];
  /** Subject used in the "Re:" line and referenced in the body. */
  subject: string;
  /** Contact name for the greeting; falls back to a generic salutation. */
  greetingName?: string;
  /** Body paragraphs rendered between greeting and sign-off. */
  bodyParagraphs?: string[];
  /** CC contact name. When set, the cover letter includes "Enclosure" and "CC: {name}". */
  ccContactDisplayName?: string;
}

export interface InvoicePDFData {
  event: {
    id?: string;
    title?: string;
    startDate?: number;
    endDate?: number;
    location?: string;
    fee?: {
      fee?: number;
      memberFee?: number;
      guestFee?: number;
      guestLateFee?: number;
      lateFee?: number;
      lateFeeDate?: number;
      noRefundDate?: number;
      currency?: string;
    };
    program?: Array<{
      id?: string;
      title?: string;
      registrationPageSessionTitle?: string;
      fee?: number;
      specialEventGuestFee?: number | null;
    }>;
  };
  person: {
    name: string;
    email?: string;
  };
  firm?: {
    name: string;
  } | null;
  badgeName?: string;
  guests?: { name: string; registrationDate?: number }[];
  guestName?: string;
  guestName2?: string;
  eventPayment?: string;
  creditPayment?: string;
  amountRefunded?: string;
  additionalFee?: string;
  paymentMethod?: string;
  paymentMethodOptions?: Array<{ id: string; name: string }>;
  paid?: "paid" | "partial" | "unpaid";
  notes?: string;
  eventId: string;
  registrationId?: string;
  personId?: string;
  filename?: string;
  address?: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  invoiceAddress?:
    | {
        address1?: string;
        address2?: string;
        city?: string;
        state?: string;
        zipCode?: string;
        country?: string;
      }
    | Array<{
        address1?: string;
        address2?: string;
        city?: string;
        state?: string;
        zipCode?: string;
        country?: string;
      }>;
  vatNumber?: string;
  regTerms?: string;
  logoImageDataUrl?: string;
  selectedSpecialEventIds?: string[];
  specialEventGuestNames?: Record<string, string | string[]>;
  registrationDate?: number;
  isPrepaid?: boolean;
  isHostFirm?: boolean;
  /** Accounting / membership invoices: use pre-built line rows instead of event fees. */
  customLineItems?: { description: string; amountText: string }[];
  /** Replaces "{eventTitle} Registration" above the line-item table. */
  invoiceSectionTitle?: string;
  /** Overrides auto-generated reference in the PDF header. */
  referenceNumber?: string;
  /** Overrides today's date in the PDF header (e.g. "6 July 2026"). */
  invoiceDateDisplay?: string;
  /** Invoice total when using customLineItems. */
  customTotalAmount?: number;
  /** Overrides the "Amount Paid" label for the paid line (custom billing). */
  paidLabel?: string;
  /** Credit balance applied; shown on paid accounting invoices as a line item. */
  creditAppliedDisplay?: number;
  /**
   * Omits the "No refund of the pre-paid fees..." boilerplate appended after
   * the note. Used by accounting invoices, where that event-specific text does
   * not apply. Events/registrations leave this false to keep the disclaimer.
   */
  hidePrepaidRefundNote?: boolean;
  /** Hides the section title row above line items (e.g. "FY2026 Membership Dues"). */
  hideInvoiceSectionTitle?: boolean;
  /** Number of times the invoice has been sent; shows "{n} reminder" under the title. */
  reminderCount?: number;
  /**
   * Optional cover letter rendered as the FIRST page. When present, the invoice
   * itself is rendered on the following page. Used by accounting invoices.
   */
  coverLetter?: InvoiceCoverLetter;
  /**
   * Italic disclaimer lines rendered inside the invoice box, just above the
   * TOTAL row (e.g. annual-fee / late-fee terms). Used by accounting invoices.
   */
  boxDisclaimerLines?: string[];
}

const formatAddress = (
  address: InvoicePDFData["address"],
  country?: string,
): string => {
  if (!address) {
    return "";
  }

  const parts: string[] = [];
  if (address.address1) {
    parts.push(address.address1);
  }
  if (address.address2) {
    parts.push(address.address2);
  }

  const countryName = address.country || country || "";
  const isUSOrCanada =
    countryName === "United States of America" || countryName === "Canada";

  if (address.city) {
    if (isUSOrCanada && address.state && address.zip) {
      parts.push(`${address.city}, ${address.state} ${address.zip}`);
    } else if (address.zip) {
      parts.push(`${address.city} ${address.zip}`);
    } else {
      parts.push(address.city);
    }
  }

  if (countryName) {
    parts.push(countryName);
  }

  return parts.join("\n");
};

const COVER_LETTER_SIGNATORY_NAME = "Mathias Schroeder";
const COVER_LETTER_SIGNATORY_TITLE_LINES = [
  "Partner, Heuking",
  "WSG Treasurer",
];
const COVER_LETTER_HEADER_ROLE = "WSG Treasurer";
const WSG_BRAND_RED: [number, number, number] = [188, 51, 35];

function drawRightAlignedText(
  doc: jsPDF,
  text: string,
  rightEdge: number,
  y: number,
): void {
  const width = doc.getTextWidth(text);
  doc.text(text, rightEdge - width, y);
}

function drawRightAlignedLineWithRedDiamonds(
  doc: jsPDF,
  fontFamily: string,
  leftText: string,
  rightText: string,
  rightEdge: number,
  y: number,
): void {
  doc.setFont(fontFamily, "normal");
  doc.setTextColor(0, 0, 0);
  const leftWidth = doc.getTextWidth(leftText);
  doc.setTextColor(...WSG_BRAND_RED);
  const diamondWidth = doc.getTextWidth("\u2666");
  doc.setTextColor(0, 0, 0);
  const rightWidth = doc.getTextWidth(rightText);
  const totalWidth = leftWidth + diamondWidth + rightWidth;
  let x = rightEdge - totalWidth;

  doc.setTextColor(0, 0, 0);
  doc.text(leftText, x, y);
  x += leftWidth;

  doc.setTextColor(...WSG_BRAND_RED);
  doc.text("\u2666", x, y);
  x += diamondWidth;

  doc.setTextColor(0, 0, 0);
  doc.text(rightText, x, y);
}

/** Renders a single line with word spacing expanded to fill `maxWidth` (last lines stay left-aligned). */
function drawJustifiedTextLine(
  doc: jsPDF,
  line: string,
  x: number,
  y: number,
  maxWidth: number,
  justify: boolean,
): void {
  const trimmed = line.trim();
  if (!trimmed) {
    return;
  }
  if (!justify) {
    doc.text(trimmed, x, y);
    return;
  }

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length <= 1) {
    doc.text(trimmed, x, y);
    return;
  }

  const wordsWidth = words.reduce(
    (sum, word) => sum + doc.getTextWidth(word),
    0,
  );
  const extraPerSpace = (maxWidth - wordsWidth) / (words.length - 1);
  let cursorX = x;
  words.forEach((word, index) => {
    doc.text(word, cursorX, y);
    if (index < words.length - 1) {
      cursorX += doc.getTextWidth(word) + extraPerSpace;
    }
  });
}

/** Draws a paragraph with justified body lines; returns the y of the last line. */
function drawJustifiedParagraph(
  doc: jsPDF,
  paragraph: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): number {
  const lines: string[] = doc.splitTextToSize(paragraph, maxWidth);
  for (let i = 0; i < lines.length; i++) {
    const isLastLine = i === lines.length - 1;
    drawJustifiedTextLine(
      doc,
      lines[i],
      x,
      y + i * lineHeight,
      maxWidth,
      !isLastLine,
    );
  }
  return y + Math.max(0, lines.length - 1) * lineHeight;
}

/**
 * Renders the accounting cover letter on the current page. Caller is responsible
 * for adding the subsequent invoice page (via doc.addPage()).
 */
const renderCoverLetterPage = (
  doc: jsPDF,
  fontFamily: string,
  cover: InvoiceCoverLetter,
  logoImageDataUrl?: string,
): void => {
  const leftX = 90;
  const rightEdge = 550;
  const bodyMaxWidth = rightEdge - leftX;
  const lineHeight = 14;

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(1);
  doc.line(70, 45, 550, 45);

  if (logoImageDataUrl) {
    try {
      const logoWidth = 80;
      const logoHeight = (57 / 161) * logoWidth;
      doc.addImage(logoImageDataUrl, "PNG", 70, 55, logoWidth, logoHeight);
    } catch (error) {
      console.error("Error adding logo to cover letter:", error);
    }
  }

  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  drawRightAlignedLineWithRedDiamonds(
    doc,
    fontFamily,
    "2777 Allen Parkway, Suite #622 ",
    " Houston Texas 77019",
    rightEdge,
    66,
  );
  drawRightAlignedLineWithRedDiamonds(
    doc,
    fontFamily,
    "713.650.0333 ",
    " www.worldservicesgroup.com",
    rightEdge,
    79,
  );

  doc.setFont(fontFamily, "bold");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  drawRightAlignedText(doc, COVER_LETTER_SIGNATORY_NAME, rightEdge, 100);

  doc.setFont(fontFamily, "normal");
  drawRightAlignedText(doc, COVER_LETTER_HEADER_ROLE, rightEdge, 114);

  doc.setFont(fontFamily, "normal");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);

  let y = 150;
  doc.text(cover.dateDisplay, leftX, y);

  y += 45;
  doc.text(cover.recipientName, leftX, y);
  cover.recipientAddressLines.forEach((line) => {
    y += lineHeight;
    doc.text(line, leftX, y);
  });

  y += lineHeight * 3;
  doc.setFont(fontFamily, "bold");
  doc.text(`Re: ${cover.subject}`, leftX, y);

  y += lineHeight * 3;
  doc.setFont(fontFamily, "normal");
  const greeting = cover.greetingName?.trim()
    ? `Dear ${cover.greetingName.trim()},`
    : "Dear Sir or Madam,";
  doc.text(greeting, leftX, y);

  const paragraphs =
    cover.bodyParagraphs && cover.bodyParagraphs.length > 0
      ? cover.bodyParagraphs
      : buildDefaultCoverLetterParagraphs(cover.subject, cover.recipientName);
  for (const paragraph of paragraphs) {
    y += lineHeight * 2;
    y = drawJustifiedParagraph(
      doc,
      paragraph,
      leftX,
      y,
      bodyMaxWidth,
      lineHeight,
    );
  }

  y += lineHeight * 3;
  doc.text("Sincerely,", leftX, y);
  y += lineHeight * 2;
  doc.text(COVER_LETTER_SIGNATORY_NAME, leftX, y);
  for (const titleLine of COVER_LETTER_SIGNATORY_TITLE_LINES) {
    y += lineHeight;
    doc.text(titleLine, leftX, y);
  }
  const ccName = cover.ccContactDisplayName?.trim();
  if (ccName) {
    y += lineHeight * 2;
    doc.text("Enclosure", leftX, y);
    y += lineHeight * 2;
    doc.text(`CC: ${ccName}`, leftX, y);
  }
};

export const generateInvoicePDF = async (
  data: InvoicePDFData,
): Promise<Blob | null> => {
  const {
    event,
    person,
    firm,
    guests,
    guestName,
    guestName2,
    eventPayment = "0",
    creditPayment = "0",
    additionalFee = "0",
    paid = "unpaid",
    eventId,
    registrationId,
    personId,
    address,
    invoiceAddress,
    vatNumber,
    regTerms,
    logoImageDataUrl,
    selectedSpecialEventIds,
    specialEventGuestNames = {},
    registrationDate,
    isPrepaid = false,
    isHostFirm = false,
    paymentMethod,
    customLineItems,
    invoiceSectionTitle,
    referenceNumber: referenceNumberOverride,
    invoiceDateDisplay,
    customTotalAmount,
    paidLabel,
    creditAppliedDisplay,
    hidePrepaidRefundNote = false,
    coverLetter,
    hideInvoiceSectionTitle = false,
    reminderCount,
    boxDisclaimerLines,
  } = data;

  const useCustomBilling =
    Array.isArray(customLineItems) && customLineItems.length > 0;

  if (!useCustomBilling && (!event || !person)) {
    alert("Event and person information are required to generate invoice");
    return null;
  }

  if (useCustomBilling && !person) {
    alert("Billing contact information is required to generate invoice");
    return null;
  }

  const paidAmount = parseFloat(eventPayment || "0");
  const creditAmount = parseFloat(creditPayment || "0");
  const isPaid = paid === "paid";

  let currency: string;
  let finalAmount: number;
  let eventTitle: string;
  let regAmount = 0;
  let applicableGuestFee = 0;
  let program: InvoicePDFData["event"]["program"];
  let additionalFeeValue = 0;

  if (useCustomBilling) {
    currency = event?.fee?.currency || "USD";
    const totalAmount = customTotalAmount ?? 0;
    const amountDue = totalAmount - paidAmount - creditAmount;
    finalAmount = isPaid ? paidAmount : Math.max(0, amountDue);
    eventTitle = event?.title || "Invoice";
  } else {
    const fee = event!.fee;
    currency = fee?.currency || "USD";

    const registrationForFees = {
      guests,
      guestName,
      guestName2,
      registrationDate,
    } as Parameters<typeof shouldUseLateFeesForRegistration>[1];

    const numberOfGuests = countRegistrationGuests(registrationForFees);
    const guestFee = fee?.guestFee || 0;
    const guestLateFee = fee?.guestLateFee || 0;
    const shouldUseLateFees = shouldUseLateFeesForRegistration(
      { fee } as Parameters<typeof shouldUseLateFeesForRegistration>[0],
      registrationForFees,
    );
    applicableGuestFee =
      shouldUseLateFees && guestLateFee > 0 ? guestLateFee : guestFee;
    const guestTotal = applicableGuestFee * numberOfGuests;
    const rawMemberFee = shouldUseLateFees
      ? (fee?.lateFee ?? fee?.memberFee ?? fee?.fee ?? 0)
      : (fee?.memberFee ?? fee?.fee ?? 0);
    const waiveMemberFee = shouldWaiveRegistrantMemberFee({
      prepaid: isPrepaid,
      isHostFirm,
      paymentMethod,
    });
    const memberFee = waiveMemberFee ? 0 : rawMemberFee;
    additionalFeeValue = parseFloat(additionalFee || "0");
    regAmount = memberFee;

    let specialEventsTotal = 0;
    program = event!.program;
    const idsToUse = data.selectedSpecialEventIds ?? [];
    if (program?.length && idsToUse.length > 0) {
      const byIdMap = new Map(
        program.filter((p: any) => p.id).map((p: any) => [p.id, p]),
      );
      for (const id of idsToUse) {
        const prog = byIdMap.get(id);
        if (!prog) {
          continue;
        }
        specialEventsTotal += prog.fee ?? 0;
        const guests = getSpecialEventGuestsForProgram(
          specialEventGuestNames,
          id,
        );
        specialEventsTotal += (prog.specialEventGuestFee ?? 0) * guests.length;
      }
    }

    const totalAmount =
      regAmount + guestTotal + additionalFeeValue + specialEventsTotal;
    const amountDue = totalAmount - paidAmount - creditAmount;
    finalAmount = isPaid ? paidAmount : amountDue;
    eventTitle = event!.title || "Event";
  }

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "letter",
  });

  let invoiceFontFamily: "NotoSans" | "helvetica" = "helvetica";
  try {
    embedNotoSans(doc, await loadNotoSansBase64());
    invoiceFontFamily = INVOICE_FONT;
  } catch (e) {
    console.warn(
      "Invoice PDF: falling back to Helvetica (Unicode may not render):",
      e,
    );
  }

  if (coverLetter) {
    renderCoverLetterPage(
      doc,
      invoiceFontFamily,
      coverLetter,
      logoImageDataUrl,
    );
    doc.addPage();
  }

  const rightEdge = 550;

  if (logoImageDataUrl) {
    try {
      const logoWidth = 80;
      const logoHeight = (57 / 161) * logoWidth;
      const logoX = 70;
      const logoY = 50;
      doc.addImage(
        logoImageDataUrl,
        "PNG",
        logoX,
        logoY,
        logoWidth,
        logoHeight,
      );
    } catch (error) {
      console.error("Error adding logo to PDF:", error);
    }
  }

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(1);

  doc.line(70, 45, 550, 45);

  const invoiceTypeText = isPaid ? "PAID INVOICE" : "INVOICE";
  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(16);
  doc.setTextColor(188, 51, 35);
  const typeWidth = doc.getTextWidth(invoiceTypeText);
  doc.text(invoiceTypeText, rightEdge - typeWidth - 5, 75);

  if (
    typeof reminderCount === "number" &&
    !Number.isNaN(reminderCount) &&
    reminderCount !== 0
  ) {
    doc.setFont(invoiceFontFamily, "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const reminderText = `#${reminderCount} reminder`;
    const reminderWidth = doc.getTextWidth(reminderText);
    doc.text(reminderText, rightEdge - reminderWidth - 5, 90);
  }

  doc.setFont(invoiceFontFamily, "normal");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);

  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(11);
  doc.text("BILL TO:", 90, 160);
  doc.text("REMIT TO:", 370, 160);

  doc.setFont(invoiceFontFamily, "normal");
  doc.setFontSize(10);

  type InvoiceLine = {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  let inv: InvoiceLine | undefined;
  if (Array.isArray(invoiceAddress) && invoiceAddress.length > 0) {
    inv = invoiceAddress[0];
  } else if (invoiceAddress && !Array.isArray(invoiceAddress)) {
    inv = invoiceAddress;
  }

  let addressToUse: InvoicePDFData["address"] | undefined;
  if (inv) {
    addressToUse = {
      address1: inv.address1,
      address2: inv.address2,
      city: inv.city,
      state: inv.state,
      zip: inv.zipCode,
      country: inv.country,
    };
  } else {
    addressToUse = address;
  }

  const billToAddress = formatAddress(addressToUse, addressToUse?.country);
  const companyName = firm?.name || "";
  const billToLines: string[] = [];
  if (companyName) {
    billToLines.push(companyName);
  }
  if (billToAddress) {
    billToLines.push(...billToAddress.split("\n"));
  }
  if (vatNumber) {
    billToLines.push(`VAT: ${vatNumber}`);
  }

  let billToY = 180;
  billToLines.forEach((line) => {
    const lines = doc.splitTextToSize(line, 240);
    lines.forEach((l: string) => {
      doc.text(l, 90, billToY);
      billToY += 12;
    });
  });

  const remitToText =
    "ATTN: Accounting Department\nWorld Services Group, Inc.\n2777 Allen Pkwy., Suite 622\nHouston, Texas 77019";
  const remitToLines = remitToText.split("\n");
  let remitToY = 180;
  remitToLines.forEach((line) => {
    doc.text(line, 370, remitToY);
    remitToY += 12;
  });

  const currentDate = new Date();
  const defaultDateStr = `${currentDate.getDate()} ${currentDate.toLocaleString("en-US", { month: "long" })} ${currentDate.getFullYear()}`;
  const dateStr = invoiceDateDisplay?.trim() || defaultDateStr;

  const resolvedEventId = eventId || event?.id || "";
  const eventNumericDigits = resolvedEventId.replace(/\D/g, "");
  const eventPrefix = eventNumericDigits.substring(0, 4).padStart(4, "0");

  const userId = personId || registrationId || "";
  const userNumericDigits = userId.replace(/\D/g, "");
  const userPrefix = userNumericDigits.substring(0, 4).padStart(4, "0");

  const referenceNumber =
    referenceNumberOverride?.trim() || `M${eventPrefix}-${userPrefix}`;

  doc.setFont(invoiceFontFamily, "normal");
  doc.setFontSize(10);
  const refNumberWidth = doc.getTextWidth(referenceNumber);
  const dateWidth = doc.getTextWidth(dateStr);

  const dateX = rightEdge - dateWidth - 5;
  const refX = rightEdge - refNumberWidth - 5;

  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(10);
  const dateLabelWidth = doc.getTextWidth("DATE:");
  const refLabelWidth = doc.getTextWidth("REFERENCE #:");
  const labelSpacing = 5;

  doc.text("DATE:", dateX - dateLabelWidth - labelSpacing, 110);

  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(10);
  doc.text(dateStr, dateX, 110);

  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(10);
  doc.text("REFERENCE #:", refX - refLabelWidth - labelSpacing, 130);

  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(10);
  doc.text(referenceNumber, refX, 130);

  const lineItems = useCustomBilling
    ? buildCustomInvoicePDFLineItems(customLineItems!, {
        currency,
        paidAmount,
        isPaid,
        paidLabel,
        creditAppliedDisplay,
      })
    : buildInvoicePDFLineItems({
        person: person!,
        regAmount,
        guestNames: normalizeRegistrationGuests({
          guests,
          guestName,
          guestName2,
          registrationDate,
        }).map((g) => g.name),
        applicableGuestFee,
        program,
        selectedSpecialEventIds,
        specialEventGuestNames,
        eventTitle,
        additionalFeeValue,
        creditAmount,
        paidAmount,
        isPaid,
        isPrepaid,
        currency,
      });

  const itemsStartOffset = hideInvoiceSectionTitle
    ? INVOICE_BOX.headerHeight + 8
    : INVOICE_BOX.itemsStartOffset;

  const effectiveTotalRowHeight = !isPaid ? 34 : INVOICE_BOX.totalRowHeight;

  const DISCLAIMER_FONT_SIZE = 8;
  const DISCLAIMER_LINE_HEIGHT = 11;
  const DISCLAIMER_BOTTOM_GAP = 8;
  const disclaimerWrapped: string[] = [];
  if (Array.isArray(boxDisclaimerLines) && boxDisclaimerLines.length > 0) {
    doc.setFont(invoiceFontFamily, "italic");
    doc.setFontSize(DISCLAIMER_FONT_SIZE);
    for (const line of boxDisclaimerLines) {
      if (!line.trim()) {
        continue;
      }
      disclaimerWrapped.push(...doc.splitTextToSize(line, DESC_MAX_WIDTH));
    }
  }
  const disclaimerBlockHeight =
    disclaimerWrapped.length > 0
      ? disclaimerWrapped.length * DISCLAIMER_LINE_HEIGHT +
        DISCLAIMER_BOTTOM_GAP
      : 0;

  const {
    boxHeight,
    boxBottom,
    totalBarY,
    totalTextY,
    itemsStartY,
    layoutShift,
    totalRowHeight,
  } = calculateInvoiceBoxLayout(
    doc,
    invoiceFontFamily,
    lineItems,
    itemsStartOffset,
    effectiveTotalRowHeight,
    disclaimerBlockHeight,
  );

  const { positive: currencySymbol } = getCurrencySymbols(currency);

  doc.setFillColor(240, 248, 255);
  doc.rect(
    INVOICE_BOX.x,
    INVOICE_BOX.top,
    INVOICE_BOX.width,
    INVOICE_BOX.headerHeight,
    "F",
  );

  doc.setFillColor(217, 217, 217);
  doc.rect(INVOICE_BOX.x, totalBarY, INVOICE_BOX.width, totalRowHeight, "F");

  doc.setDrawColor(0, 0, 0);
  doc.rect(INVOICE_BOX.x, INVOICE_BOX.top, INVOICE_BOX.width, boxHeight);
  doc.line(
    INVOICE_BOX.amountDividerX,
    INVOICE_BOX.top,
    INVOICE_BOX.amountDividerX,
    boxBottom,
  );

  doc.setFont(invoiceFontFamily, "normal");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text("Description", 230, INVOICE_BOX.top + 13);
  doc.text("Amount", 480, INVOICE_BOX.top + 13);

  if (!hideInvoiceSectionTitle) {
    doc.setFont(invoiceFontFamily, "bold");
    doc.setFontSize(10);
    const eventTitleText =
      invoiceSectionTitle?.trim() || `${eventTitle} Registration`;
    const eventTitleWidth = doc.getTextWidth(eventTitleText);
    doc.text(
      eventTitleText,
      INVOICE_BOX.amountDividerX - eventTitleWidth - 5,
      INVOICE_BOX.top + INVOICE_BOX.eventTitleOffset,
    );
  }

  doc.setFont(invoiceFontFamily, "normal");
  doc.setFontSize(10);

  let descriptionY = itemsStartY;
  for (const item of lineItems) {
    descriptionY = drawDescriptionAmountRow(
      doc,
      invoiceFontFamily,
      item.description,
      item.amountText,
      descriptionY,
    );
  }

  if (disclaimerWrapped.length > 0) {
    doc.setFont(invoiceFontFamily, "italic");
    doc.setFontSize(DISCLAIMER_FONT_SIZE);
    doc.setTextColor(0, 0, 0);
    const lastLineY = totalBarY - DISCLAIMER_BOTTOM_GAP;
    const firstLineY =
      lastLineY - (disclaimerWrapped.length - 1) * DISCLAIMER_LINE_HEIGHT;
    disclaimerWrapped.forEach((line, i) => {
      doc.text(line, DESC_LEFT_X, firstLineY + i * DISCLAIMER_LINE_HEIGHT);
    });
  }

  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(11);
  const totalTypeText = `TOTAL ${isPaid ? "PAID" : "DUE"}`;
  const totalLabelY = !isPaid ? totalBarY + 11 : totalTextY;
  doc.text(totalTypeText, 370, totalLabelY);
  drawJustifiedAmount(
    doc,
    `${currencySymbol}${formatAmount(Math.max(0, finalAmount))}`,
    totalLabelY,
  );
  if (!isPaid) {
    doc.setFont(invoiceFontFamily, "normal");
    doc.setFontSize(8);
    doc.text("(if paid by month's end)", 340, totalBarY + 22);
  }

  // Lift the whole footer up to compensate for the raised invoice box, keeping
  // the box-to-footer gap constant and leaving room for the closing note.
  const FOOTER_LIFT = 25;
  const footerNeedsNewPage = layoutShift + 430 + 280 > PAGE_HEIGHT;
  if (footerNeedsNewPage) {
    doc.addPage();
  }
  const footerYOffset = footerNeedsNewPage
    ? 70 - 430
    : layoutShift - FOOTER_LIFT;
  const footerY = (y: number) => y + footerYOffset;

  if (isPaid && regTerms && regTerms.trim()) {
    doc.setDrawColor(0, 0, 0);
    doc.rect(INVOICE_BOX.x, footerY(430), INVOICE_BOX.width, 60);
    doc.setFont(invoiceFontFamily, "normal");
    doc.setFontSize(10);
    doc.setTextColor(128, 0, 0);
    const noteText = `NOTE: ${regTerms.replace(/\n/g, " ")}`;
    const noteLines = doc.splitTextToSize(noteText, 470);
    let noteY = footerY(445);
    noteLines.forEach((line: string) => {
      doc.text(line, 80, noteY);
      noteY += LINE_HEIGHT;
    });
  } else if (!isPaid) {
    doc.setDrawColor(0, 0, 0);
    doc.rect(INVOICE_BOX.x, footerY(445), 240, 157);
    doc.rect(310, footerY(445), 240, 157);
    doc.line(310, footerY(445), 310, footerY(602));
    const creditCardContainerHeight = regTerms && regTerms.trim() ? 186 : 102;
    doc.rect(
      INVOICE_BOX.x,
      footerY(602),
      INVOICE_BOX.width,
      creditCardContainerHeight,
    );

    doc.setFont(invoiceFontFamily, "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("Payment Options: ", INVOICE_BOX.x, footerY(434));
    doc.text("CHECK:", 77, footerY(458));
    doc.text("WIRE TRANSFER:", 320, footerY(458));
    doc.text("CREDIT CARD:", 77, footerY(616));

    doc.setFont(invoiceFontFamily, "italic");
    doc.setFontSize(7);
    doc.text("Drawn on U.S. bank payable in U.S. Dollars", 77, footerY(471));
    doc.text(
      "Please provide your wire transfer reference #",
      320,
      footerY(471),
    );
    doc.text(
      "(A 5% fee will be applied to credit card payments.)",
      77,
      footerY(629),
    );

    doc.setFont(invoiceFontFamily, "normal");
    doc.setFontSize(9);
    const checkPayableText =
      "Make Checks Payable to:\n\nWorld Services Group, Inc.\n2777 Allen Pkwy., Suite #622\nHouston, Texas 77019";
    const checkLines = checkPayableText.split("\n");
    let checkY = footerY(491);
    checkLines.forEach((line) => {
      doc.text(line, 77, checkY);
      checkY += LINE_HEIGHT;
    });

    const refY = checkY + LINE_HEIGHT;
    doc.text(`REF: ${referenceNumber}`, 77, refY);

    const wireTransferText =
      "Bank of America\n901 Main Street\nDallas, Texas 75202\nUSA\n\nABA NO. 0260-0959-3\nCredit to:\nWorld Services Group, Inc.\nAccount No: 005740190806\nSWIFT: BOFAUS3N";
    const wireLines = wireTransferText.split("\n");
    let wireY = footerY(491);
    wireLines.forEach((line) => {
      doc.text(line, 320, wireY);
      wireY += LINE_HEIGHT;
    });

    doc.setFont(invoiceFontFamily, "normal");
    doc.setFontSize(9);

    const visaLabelText = "VISA  MC  AMEX  Other:";
    doc.text(visaLabelText, 77, footerY(644));
    const visaLabelWidth = doc.getTextWidth(visaLabelText);
    doc.line(
      77 + visaLabelWidth + 2,
      footerY(646),
      77 + visaLabelWidth + 2 + 200,
      footerY(646),
    );

    const cardNumberLabelText = "Card Number:";
    doc.text(cardNumberLabelText, 77, footerY(659));
    const cardNumberLabelWidth = doc.getTextWidth(cardNumberLabelText);
    const cardNumberUnderlineWidth = doc.getTextWidth("0") * 20;
    doc.line(
      77 + cardNumberLabelWidth + 2,
      footerY(661),
      77 + cardNumberLabelWidth + 2 + cardNumberUnderlineWidth,
      footerY(661),
    );

    const expLabelText = "Exp.:";
    doc.text(
      expLabelText,
      77 + cardNumberLabelWidth + 10 + cardNumberUnderlineWidth,
      footerY(659),
    );
    const expLabelWidth = doc.getTextWidth(expLabelText);
    const expUnderlineWidth = doc.getTextWidth("0") * 8;
    doc.line(
      77 +
        cardNumberLabelWidth +
        10 +
        cardNumberUnderlineWidth +
        expLabelWidth +
        2,
      footerY(661),
      77 +
        cardNumberLabelWidth +
        10 +
        cardNumberUnderlineWidth +
        expLabelWidth +
        2 +
        expUnderlineWidth,
      footerY(661),
    );

    const cardHolderLabelText = "Card Holder Name:";
    doc.text(cardHolderLabelText, 77, footerY(674));
    const cardHolderLabelWidth = doc.getTextWidth(cardHolderLabelText);
    const cardHolderUnderlineWidth = doc.getTextWidth("M") * 30;
    doc.line(
      77 + cardHolderLabelWidth + 2,
      footerY(676),
      77 + cardHolderLabelWidth + 2 + cardHolderUnderlineWidth,
      footerY(676),
    );

    const billingLabelText = "Billing Address:";
    doc.text(billingLabelText, 77, footerY(689));
    const billingLabelWidth = doc.getTextWidth(billingLabelText);
    const billingUnderlineWidth = doc.getTextWidth("M") * 40;
    doc.line(
      77 + billingLabelWidth + 2,
      footerY(691),
      77 + billingLabelWidth + 2 + billingUnderlineWidth,
      footerY(691),
    );

    doc.setDrawColor(0, 0, 0);
    doc.line(70, footerY(704), 550, footerY(704));

    if (regTerms && regTerms.trim()) {
      doc.setFont(invoiceFontFamily, "normal");
      doc.setFontSize(9);
      doc.setTextColor(128, 0, 0);
      const termsText = hidePrepaidRefundNote
        ? `NOTE: ${regTerms.replace(/\n/g, " ")}`
        : `NOTE: ${regTerms.replace(/\n/g, " ")}\n\nNo refund of the pre-paid fees. If the member does not attend, the fees cannot be rolled over and the fees will not be refunded.`;
      const termsLines = doc.splitTextToSize(termsText, 460);
      let termsY = footerY(708);
      termsLines.forEach((line: string) => {
        doc.text(line, 75, termsY);
        termsY += LINE_HEIGHT;
      });
    }

    const paymentBoxBottom = footerY(602) + creditCardContainerHeight;
    doc.setFont(invoiceFontFamily, "italic");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text(
      "All funds must be sent NET of bank charges failing which will regard the wire as incomplete payment.",
      INVOICE_BOX.x,
      paymentBoxBottom + 13,
    );
  }

  const pdfBlob = doc.output("blob");
  return pdfBlob;
};

export const openInvoicePDF = async (
  pdfBlobOrUrl: Blob | string,
  filename?: string,
): Promise<void> => {
  let url: string;
  let shouldRevokeUrl = false;

  if (typeof pdfBlobOrUrl === "string") {
    const isBlobStorageUrl = pdfBlobOrUrl.includes("blob.core.windows.net");

    if (isBlobStorageUrl) {
      try {
        const response = await fetch(pdfBlobOrUrl);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch PDF: ${response.status} ${response.statusText}`,
          );
        }
        const blobData = await response.blob();
        const blob = new Blob([blobData], { type: "application/pdf" });
        url = window.URL.createObjectURL(blob);
        shouldRevokeUrl = true;
      } catch (error) {
        console.error("Error fetching PDF from blob storage:", error);
        url = pdfBlobOrUrl;
      }
    } else {
      url = pdfBlobOrUrl;
    }
  } else {
    url = window.URL.createObjectURL(pdfBlobOrUrl);
    shouldRevokeUrl = true;
  }

  try {
    const newWindow = window.open(url, "_blank", "noopener,noreferrer");

    if (!newWindow) {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    if (shouldRevokeUrl) {
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    }
  } catch (error) {
    console.error("Error opening invoice PDF:", error);
    if (shouldRevokeUrl) {
      window.URL.revokeObjectURL(url);
    }
    throw error;
  }
};

/**
 * Downloads a PDF file instead of opening it in a new tab.
 * Can accept either a Blob or a URL string.
 * For blob storage URLs, fetches the blob and creates a local object URL to download.
 *
 * @param pdfBlobOrUrl - The PDF as a Blob or a URL string
 * @param filename - Filename for the download
 */
export const downloadInvoicePDF = async (
  pdfBlobOrUrl: Blob | string,
  filename: string,
): Promise<void> => {
  let url: string;
  let shouldRevokeUrl = false;
  let blob: Blob;

  if (typeof pdfBlobOrUrl === "string") {
    const isBlobStorageUrl = pdfBlobOrUrl.includes("blob.core.windows.net");

    if (isBlobStorageUrl) {
      try {
        const response = await fetch(pdfBlobOrUrl);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch PDF: ${response.status} ${response.statusText}`,
          );
        }
        blob = await response.blob();
        url = window.URL.createObjectURL(blob);
        shouldRevokeUrl = true;
      } catch (error) {
        console.error("Error fetching PDF from blob storage:", error);
        throw error;
      }
    } else {
      try {
        const response = await fetch(pdfBlobOrUrl);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch PDF: ${response.status} ${response.statusText}`,
          );
        }
        blob = await response.blob();
        url = window.URL.createObjectURL(blob);
        shouldRevokeUrl = true;
      } catch (error) {
        console.error("Error fetching PDF:", error);
        throw error;
      }
    }
  } else {
    blob = pdfBlobOrUrl;
    url = window.URL.createObjectURL(blob);
    shouldRevokeUrl = true;
  }

  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "invoice.pdf";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error downloading invoice PDF:", error);
    throw error;
  } finally {
    if (shouldRevokeUrl) {
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    }
  }
};

export const convertLogoSvgToImage = async (): Promise<string | null> => {
  try {
    const logoSvgModule = await import("../assets/images&icons/logo-wsg.svg");
    const logoSvgUrl = logoSvgModule.default || logoSvgModule;

    if (!logoSvgUrl || typeof logoSvgUrl !== "string") {
      return null;
    }

    const response = await fetch(logoSvgUrl);
    const svgText = await response.text();

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return null;
    }

    const img = new Image();
    const svgBlob = new Blob([svgText], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);

    return new Promise((resolve) => {
      img.onload = () => {
        canvas.width = 161;
        canvas.height = 57;
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL("image/png");
        URL.revokeObjectURL(url);
        resolve(dataUrl);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  } catch (error) {
    console.error("Error converting logo SVG to image:", error);
    return null;
  }
};

export const generateInvoicePDFWithLogo = async (
  data: InvoicePDFData,
): Promise<Blob | null> => {
  if (!data.logoImageDataUrl) {
    const logoDataUrl = await convertLogoSvgToImage();
    if (logoDataUrl) {
      data.logoImageDataUrl = logoDataUrl;
    }
  }

  return await generateInvoicePDF(data);
};

export const saveInvoicePDFToBlob = async (
  pdfBlob: Blob,
  filename: string,
  personId: string,
  eventId: string,
): Promise<string | undefined> => {
  try {
    const reader = new FileReader();
    return new Promise<string | undefined>((resolve, reject) => {
      reader.onload = async () => {
        if (reader.result && typeof reader.result === "string") {
          const base64FileData = reader.result.split(",")[1];
          const extension = "pdf";
          const fileNameWithoutExtension = filename.replace(/\.pdf$/i, "");

          try {
            const Axios = (await import("./axios")).default;
            const axios = new Axios();

            const file = {
              fileData: base64FileData,
              fileName: fileNameWithoutExtension.trim(),
              extension: extension,
              path: `user/profile-pictures/${personId}/EventInvoices/${eventId}`,
            };

            const response = await axios.Post("/upload-files", file);

            if (response.status === 200) {
              const link = response.data.image as string;
              const storagePath = `user/profile-pictures/${personId}/EventInvoices/${eventId}/${fileNameWithoutExtension}.${extension}`;

              if (link && link.startsWith("http")) {
                resolve(link);
              } else {
                resolve(storagePath);
              }
            } else {
              console.error(
                "Error uploading PDF. Status:",
                response.status,
                "Data:",
                response.data,
              );
              resolve(undefined);
            }
          } catch (error) {
            console.error("Error uploading PDF:", error);
            resolve(undefined);
          }
        }
      };
      reader.onerror = () => {
        console.error("Error reading PDF blob");
        resolve(undefined);
      };
      reader.readAsDataURL(pdfBlob);
    });
  } catch (error) {
    console.error("Error handling PDF upload:", error);
    return undefined;
  }
};
